import React, { useEffect, useMemo, useState } from "react";
import { format, parseISO, eachDayOfInterval } from "date-fns";
import PageHero from "@/components/site/PageHero";
import Reveal from "@/components/site/Reveal";
import SEO from "@/components/site/SEO";
import { DecorationField } from "@/components/site/Decorations";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, AlertTriangle, Download, RefreshCw } from "lucide-react";

// 2026–2027 school year window for the month navigator (events themselves sync live)
const SCHOOL_YEAR_MONTHS = [
  [2026, 6], [2026, 7], [2026, 8], [2026, 9], [2026, 10], [2026, 11],
  [2027, 0], [2027, 1], [2027, 2], [2027, 3], [2027, 4], [2027, 5],
];

// Keyword-based colour coding for live Google Calendar event titles
function classifyEvent(title) {
  const t = title.toLowerCase();
  if (
    t.includes("first day") ||
    t.includes("last day") ||
    t.includes("session begins") ||
    t.includes("session ends")
  )
    return "highlight";
  if (t.includes("staff development")) return "staff";
  if (t.includes("conference")) return "event";
  if (
    t.includes("closed") ||
    t.includes("recess") ||
    t.includes("break") ||
    t.includes("no school") ||
    t.includes("holiday")
  )
    return "holiday";
  return "event";
}

const emergencyPlan = [
  {
    title: "Instructional Continuity",
    desc: "Children continue learning through live remote lessons, recorded mini-classes, and individualized activity packets aligned to each child's IEP goals. Teachers adapt routines so the rhythm of the school day stays familiar — circle time, small groups, sensory breaks — even from home.",
  },
  {
    title: "Communication Protocols",
    desc: "Families receive daily updates through email, phone calls, and our secure family messaging platform. We share schedules, links, and learning goals in clear language, and our team is available throughout the day for questions, check-ins, and quick problem-solving.",
  },
  {
    title: "Staff Training",
    desc: "Our educators and therapists are trained in remote engagement tools, digital accessibility, and trauma-informed care so children feel safe, seen, and supported on screen. Ongoing professional development keeps remote sessions interactive, multisensory, and IEP-aligned.",
  },
  {
    title: "Access to Resources",
    desc: "We provide take-home learning kits, hands-on materials, devices, internet support, and digital subscriptions as needed. No family should have to choose between connectivity and continuity — we work directly with caregivers to remove every barrier to learning.",
  },
  {
    title: "Progress Monitoring",
    desc: "Therapists and teachers track each child's progress through structured observations, video clips, and family input, then meet regularly to adjust plans collaboratively. Goals stay live and responsive, so growth keeps moving forward — even during a disruption.",
  },
  {
    title: "Emergency Contact Information",
    desc: "Up-to-date emergency contacts, medical notes, and pickup authorizations are kept on file and confirmed at every parent–teacher conference. Multiple channels are tested in advance so families and staff can reach one another quickly when it matters most.",
  },
];

const TYPE_CHIP = {
  highlight: "bg-brand-green",
  holiday: "bg-red-500",
  staff: "bg-amber-500",
  event: "bg-brand-purple",
};

function SchoolCalendar({ events, status, syncedAt }) {
  const months = useMemo(() => SCHOOL_YEAR_MONTHS.map(([y, m]) => new Date(y, m, 1)), []);
  // Default to the real-world current month; clamp to the school-year window if outside it
  const [idx, setIdx] = useState(() => {
    const now = new Date();
    const found = months.findIndex(
      (m) => m.getFullYear() === now.getFullYear() && m.getMonth() === now.getMonth()
    );
    if (found >= 0) return found;
    return now < months[0] ? 0 : months.length - 1;
  });
  const month = months[idx];

  const normalized = useMemo(
    () =>
      events.map((e) => ({
        ...e,
        startObj: parseISO(e.start),
        endObj: parseISO(e.end),
        type: classifyEvent(e.title),
      })),
    [events]
  );

  // Grid: highlight every day each event spans
  const gridDates = useMemo(
    () =>
      normalized.flatMap((e) =>
        eachDayOfInterval({ start: e.startObj, end: e.endObj }).map((d) => ({ ...e, dateObj: d }))
      ),
    [normalized]
  );

  const byType = (t) => gridDates.filter((e) => e.type === t).map((e) => e.dateObj);

  // Side panel: events overlapping the visible month
  const panelItems = useMemo(() => {
    const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
    const monthEnd = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    return normalized
      .filter((e) => e.startObj <= monthEnd && e.endObj >= monthStart)
      .sort((a, b) => a.startObj - b.startObj);
  }, [normalized, month]);

  return (
    <div className="grid lg:grid-cols-5 gap-8 items-start" data-testid="school-calendar">
      <div className="lg:col-span-3 bg-white rounded-[2rem] shadow-md border border-gray-100 p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <button
            data-testid="cal-prev"
            disabled={idx === 0}
            onClick={() => setIdx((p) => Math.max(0, p - 1))}
            className="h-10 w-10 rounded-full bg-brand-green-soft text-brand-green-deep hover:bg-brand-green hover:text-white transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous month"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-purple font-bold">Live School Calendar</div>
            <div className="font-display text-2xl font-bold text-gray-900">{format(month, "MMMM yyyy")}</div>
          </div>
          <button
            data-testid="cal-next"
            disabled={idx === months.length - 1}
            onClick={() => setIdx((p) => Math.min(months.length - 1, p + 1))}
            className="h-10 w-10 rounded-full bg-brand-green-soft text-brand-green-deep hover:bg-brand-green hover:text-white transition-colors flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next month"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
        <Calendar
          mode="single"
          month={month}
          onMonthChange={(m) => {
            const found = months.findIndex(
              (x) => x.getMonth() === m.getMonth() && x.getFullYear() === m.getFullYear()
            );
            if (found >= 0) setIdx(found);
          }}
          fromMonth={months[0]}
          toMonth={months[months.length - 1]}
          showOutsideDays={false}
          modifiers={{
            highlight: byType("highlight"),
            holiday: byType("holiday"),
            staff: byType("staff"),
            event: byType("event"),
          }}
          modifiersClassNames={{
            highlight: "bg-brand-green text-white font-bold rounded-full",
            holiday: "bg-red-100 text-red-700 font-bold rounded-full",
            staff: "bg-amber-100 text-amber-700 font-bold rounded-full",
            event: "bg-brand-purple-soft text-brand-purple-deep font-bold rounded-full",
          }}
          classNames={{
            caption: "hidden",
            head_cell: "text-brand-purple font-bold uppercase text-[11px] w-12",
            cell: "w-12 h-12 text-center text-sm p-0",
            day: "h-11 w-11 rounded-full hover:bg-brand-green-soft text-gray-700",
            row: "flex w-full mt-2 justify-between",
            head_row: "flex w-full justify-between",
            table: "w-full",
          }}
        />
        <div className="mt-5 flex flex-wrap gap-4 text-xs text-gray-600">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-brand-green" />First / Last Day</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-brand-purple-soft border border-brand-purple" />Conference</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-amber-100 border border-amber-400" />Staff Development</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-red-100 border border-red-400" />School Closed</span>
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-gray-500" data-testid="calendar-sync-note">
          <RefreshCw className="h-3.5 w-3.5 text-brand-green" />
          Synced live from the school's Google Calendar
          {syncedAt && ` — updated ${format(parseISO(syncedAt), "MMM d, h:mm a")}`}
        </p>
      </div>

      <div className="lg:col-span-2">
        <div className="bg-gradient-to-br from-brand-purple-soft to-brand-green-soft rounded-[2rem] p-6 md:p-8">
          <h3 className="font-display text-xl font-bold text-gray-900">
            {format(month, "MMMM")} Events
          </h3>
          <ul className="mt-4 space-y-3" data-testid="event-list">
            {status === "loading" && (
              <li className="text-sm text-gray-500 italic flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" /> Loading live calendar…
              </li>
            )}
            {status === "error" && (
              <li className="text-sm text-gray-500 italic">
                Unable to load the live calendar right now — please check back soon.
              </li>
            )}
            {status === "ready" && panelItems.length === 0 && (
              <li className="text-sm text-gray-500 italic">No scheduled events this month.</li>
            )}
            {status === "ready" &&
              panelItems.map((e) => (
              <li key={e.id + e.start} className="flex items-start gap-3 bg-white/85 backdrop-blur rounded-2xl p-4 shadow-sm">
                <div className={`shrink-0 text-center ${TYPE_CHIP[e.type]} text-white rounded-xl w-12 py-1`}>
                  <div className="text-[10px] uppercase tracking-widest">{format(e.startObj, "MMM")}</div>
                  <div className="font-display font-bold text-lg leading-none">{format(e.startObj, "d")}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 text-[15px]">{e.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {e.startObj.getTime() === e.endObj.getTime()
                      ? format(e.startObj, "EEEE")
                      : `${format(e.startObj, "MMM d")} – ${format(e.endObj, "MMM d")}`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="mt-5 text-sm text-gray-500 italic flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          Calendar above may be subject to change, especially if hazardous conditions occur.
        </p>
      </div>
    </div>
  );
}

function LiveSchoolCalendar() {
  const [state, setState] = useState({ status: "loading", events: [], syncedAt: null });
  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/calendar-events`)
      .then((r) => {
        if (!r.ok) throw new Error("Calendar fetch failed");
        return r.json();
      })
      .then((data) => {
        if (!cancelled)
          setState({ status: "ready", events: data.events || [], syncedAt: data.syncedAt || null });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error", events: [], syncedAt: null });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return <SchoolCalendar events={state.events} status={state.status} syncedAt={state.syncedAt} />;
}

export default function TermDatesPage() {
  return (
    <div data-testid="term-dates-page" className="overflow-hidden">
      <SEO
        title="Term Dates & Planning"
        description="View Innovators Prep's 2026–2027 school calendars for Preschool and 2K programs — key dates, closures, conferences, and school uniform guidelines."
      />
      <PageHero title="Term Dates & Planning" crumb="Term Dates & Planning" />

      {/* Calendar */}
      <section className="relative py-20 md:py-28 bg-brand-purple-soft/30">
        <DecorationField variant="mixed" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-brand-green-soft text-brand-green-deep text-xs font-bold tracking-widest uppercase">
              School Year 2026–2027
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              School Calendar
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Synced live from our Google Calendar — always up to date with key dates, breaks, and
              important events.
            </p>
          </Reveal>

          <div className="mt-12">
            <LiveSchoolCalendar />
          </div>
        </div>
      </section>

      {/* School Uniform */}
      <section className="relative py-20 md:py-28 bg-white" data-testid="uniform-section">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative rounded-[2.5rem] bg-brand-cream border border-gray-100 shadow-md overflow-hidden p-4 md:p-6">
              <img
                src="https://customer-assets-cm19k8pv.emergentagent.net/job_early-learning-hub-28/artifacts/2zvmml0c_Copy%20of%20uniform%20list.png"
                alt="Innovators Prep school uniform sheet showing approved Monday to Thursday uniform items and Friday dress down guidelines"
                className="w-full h-auto rounded-[1.75rem]"
                data-testid="uniform-sheet-image"
              />
            </div>
            <div className="mt-6 flex justify-center">
              <Button
                asChild
                variant="outline"
                data-testid="uniform-download-button"
                className="rounded-full px-6 py-5 text-sm font-semibold bg-white/90 hover:bg-white text-brand-purple-deep border-white shadow-sm"
              >
                <a
                  href="https://customer-assets-cm19k8pv.emergentagent.net/job_early-learning-hub-28/artifacts/vr1jeu07_Innovators_Prep_Uniform_Sheet.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  download="Innovators_Prep_Uniform_Sheet.pdf"
                >
                  <Download className="h-4 w-4 mr-1" /> Download PDF
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Remote Emergency Plan */}
      <section className="relative py-20 md:py-28 bg-brand-cream">
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold tracking-widest uppercase">
              Continuity of Learning
            </span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              Remote Emergency Plan
            </h2>
            <p className="mt-4 text-gray-600 text-lg">
              Should remote learning be required, our families and children stay supported through:
            </p>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {emergencyPlan.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="bg-white rounded-[1.5rem] border border-gray-100 shadow-sm hover:shadow-md transition-shadow p-6 h-full">
                  <div className="h-10 w-10 rounded-xl bg-brand-purple-soft text-brand-purple-deep flex items-center justify-center font-display font-bold">
                    {i + 1}
                  </div>
                  <h3 className="mt-4 font-display text-lg font-semibold text-gray-900">{p.title}</h3>
                  <p className="mt-2 text-[15px] text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
